import type { CSSProperties } from 'react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { cn } from '@/lib/utils';

/**
 * Heading component - renders h1-h6 elements.
 */
export function Heading({ id, props, style }: SDUIComponentProps) {
  const {
    text,
    level = 1,
    className,
  } = props as {
    text: string;
    level?: 1 | 2 | 3 | 4 | 5 | 6;
    className?: string;
  };

  const levelClasses = {
    1: 'text-3xl font-bold',
    2: 'text-2xl font-bold',
    3: 'text-xl font-semibold',
    4: 'text-lg font-semibold',
    5: 'text-base font-medium',
    6: 'text-sm font-medium',
  };

  const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  return (
    <Tag
      id={id}
      className={cn('text-foreground', levelClasses[level], className)}
      style={style as CSSProperties}
    >
      {text}
    </Tag>
  );
}

/**
 * Text component - renders paragraph text with optional streaming indicator.
 */
export function Text({ id, props, style }: SDUIComponentProps) {
  const {
    text,
    content, // Alias for text (builder format uses content)
    size = 'md',
    color = 'default',
    isStreaming = false,
    className,
  } = props as {
    text?: string;
    content?: string;
    size?: 'sm' | 'md' | 'lg';
    color?: 'default' | 'muted' | 'primary' | 'error' | 'success';
    isStreaming?: boolean;
    className?: string;
  };

  // Use text or content (for builder format compatibility)
  const displayText = text || content;

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const colorClasses = {
    default: 'text-foreground',
    muted: 'text-muted-foreground',
    primary: 'text-primary',
    error: 'text-destructive',
    success: 'text-green-600 dark:text-green-400',
  };

  return (
    <p
      id={id}
      className={cn(sizeClasses[size], colorClasses[color], className)}
      style={style as CSSProperties}
    >
      {displayText}
      {isStreaming && (
        <span
          className="inline-block w-2 h-4 ml-0.5 bg-current animate-pulse"
          style={{ verticalAlign: 'text-bottom' }}
          aria-hidden="true"
        />
      )}
    </p>
  );
}

/**
 * Label component - renders a label element for form inputs.
 */
export function Label({ id, props, style }: SDUIComponentProps) {
  const {
    text,
    htmlFor,
    required = false,
    className,
  } = props as {
    text: string;
    htmlFor?: string;
    required?: boolean;
    className?: string;
  };

  return (
    <label
      id={id}
      htmlFor={htmlFor}
      className={cn('block text-sm font-medium text-foreground', className)}
      style={style as CSSProperties}
    >
      {text}
      {required && <span className="text-destructive ml-1">*</span>}
    </label>
  );
}

/**
 * Badge component - renders a small status indicator.
 */
export function Badge({ id, props, style }: SDUIComponentProps) {
  const {
    text,
    variant = 'default',
    size = 'md',
    className,
  } = props as {
    text: string;
    variant?: 'default' | 'primary' | 'success' | 'warning' | 'error';
    size?: 'sm' | 'md';
    className?: string;
  };

  const variantClasses = {
    default: 'bg-secondary text-secondary-foreground',
    primary: 'bg-primary/20 text-primary',
    success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    error: 'bg-destructive/20 text-destructive',
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };

  return (
    <span
      id={id}
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      style={style as CSSProperties}
    >
      {text}
    </span>
  );
}
